import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import type { AdminFeatures, Order, OrderStatus, StoreSender } from '../types';

const STATUSES: Array<{ id: OrderStatus | 'all'; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'pending', label: 'Pendientes' },
  { id: 'paid', label: 'Pagados' },
  { id: 'preparing', label: 'En preparacion' },
  { id: 'shipped', label: 'Enviados' },
  { id: 'delivered', label: 'Entregados' },
  { id: 'cancelled', label: 'Cancelados' },
  { id: 'refunded', label: 'Reembolsados' }
];

const NEXT_STATUS_OPTIONS: Array<{ value: OrderStatus; label: string }> = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagado' },
  { value: 'preparing', label: 'En preparacion' },
  { value: 'shipped', label: 'Enviado' },
  { value: 'delivered', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'refunded', label: 'Reembolsado' }
];

export function OrdersPanel({
  orders,
  features,
  storeSender,
  reload
}: {
  orders: Order[];
  features: AdminFeatures;
  storeSender: StoreSender;
  reload: () => Promise<void>;
}) {
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [labelOrders, setLabelOrders] = useState<Order[] | null>(null);

  const filtered = useMemo(() => {
    const lowerSearch = search.toLowerCase().trim();
    return orders.filter((order) => {
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;
      if (!lowerSearch) return true;
      const haystack = [
        String(order.orderNumber),
        order.customerName,
        order.customerEmail,
        order.customerPhone,
        order.shippingCity,
        order.shippingAddress,
        order.trackingNumber
      ].join(' ').toLowerCase();
      return haystack.includes(lowerSearch);
    });
  }, [orders, statusFilter, search]);

  const counts = useMemo(() => {
    const map = new Map<OrderStatus | 'all', number>();
    map.set('all', orders.length);
    for (const status of STATUSES) {
      if (status.id === 'all') continue;
      map.set(status.id, orders.filter((order) => order.status === status.id).length);
    }
    return map;
  }, [orders]);

  function printPendingBatch() {
    const batch = orders.filter((order) => ['paid', 'preparing'].includes(order.status));
    if (batch.length === 0) {
      window.alert('No hay pedidos pagados o en preparacion para imprimir.');
      return;
    }
    setLabelOrders(batch);
  }

  return (
    <section className="admin-card orders-panel">
      <div className="admin-card-head">
        <div>
          <h2>Pedidos</h2>
          <p className="muted">{orders.length} pedidos en los ultimos 200 registros.</p>
        </div>
        <div className="header-actions-row">
          <button type="button" className="ghost-button compact" onClick={printPendingBatch}>
            Imprimir etiquetas pendientes
          </button>
        </div>
      </div>

      <div className="orders-toolbar">
        <div className="status-filter-row" role="tablist" aria-label="Filtrar por estado">
          {STATUSES.map((status) => {
            const count = counts.get(status.id) || 0;
            return (
              <button
                key={status.id}
                type="button"
                role="tab"
                aria-selected={statusFilter === status.id}
                className={classNames('status-filter', statusFilter === status.id && 'active')}
                onClick={() => setStatusFilter(status.id)}
              >
                {status.label} <small>{count}</small>
              </button>
            );
          })}
        </div>
        <input
          className="orders-search"
          type="search"
          placeholder="Buscar por nombre, email, numero o ciudad"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state subtle">
          <p>No hay pedidos en este filtro.</p>
          <p className="muted">Cuando entren compras nuevas apareceran aca con su estado.</p>
        </div>
      ) : (
        <div className="orders-table" role="table">
          <div className="orders-row orders-head" role="row">
            <span>#</span>
            <span>Cliente</span>
            <span>Ciudad</span>
            <span>Total</span>
            <span>Estado</span>
            <span>Acciones</span>
          </div>
          {filtered.map((order) => (
            <div className="orders-row" role="row" key={order.id}>
              <strong>#{order.orderNumber}</strong>
              <div className="orders-customer">
                <span>{order.customerName}</span>
                <small>{order.customerEmail}</small>
              </div>
              <span>{order.shippingCity || '-'}</span>
              <span>{formatCLP(order.total)}</span>
              <OrderStatusPill status={order.status} />
              <div className="orders-actions">
                <button type="button" className="ghost-button compact" onClick={() => setOpenOrderId(order.id)}>
                  Ver
                </button>
                <button type="button" className="ghost-button compact" onClick={() => setLabelOrders([order])}>
                  Etiqueta
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {openOrderId && (
        <OrderDetailModal
          orderId={openOrderId}
          features={features}
          onClose={() => setOpenOrderId(null)}
          onUpdated={async () => {
            await reload();
          }}
          onPrintLabel={(order) => setLabelOrders([order])}
        />
      )}

      {labelOrders && (
        <LabelPrintOverlay orders={labelOrders} sender={storeSender} onClose={() => setLabelOrders(null)} />
      )}
    </section>
  );
}

export function OrderStatusPill({ status }: { status: OrderStatus | string }) {
  const map: Record<string, { label: string; className: string }> = {
    pending: { label: 'Pendiente', className: 'pill-pending' },
    paid: { label: 'Pagado', className: 'pill-paid' },
    preparing: { label: 'En preparacion', className: 'pill-preparing' },
    shipped: { label: 'Enviado', className: 'pill-shipped' },
    delivered: { label: 'Entregado', className: 'pill-delivered' },
    cancelled: { label: 'Cancelado', className: 'pill-cancelled' },
    refunded: { label: 'Reembolsado', className: 'pill-refunded' }
  };
  const config = map[status] || { label: status, className: '' };
  return <span className={classNames('order-pill', config.className)}>{config.label}</span>;
}

function OrderDetailModal({
  orderId,
  features,
  onClose,
  onUpdated,
  onPrintLabel
}: {
  orderId: string;
  features: AdminFeatures;
  onClose: () => void;
  onUpdated: () => Promise<void>;
  onPrintLabel: (order: Order) => void;
}) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [generatingStarken, setGeneratingStarken] = useState(false);
  const [error, setError] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [trackingDraft, setTrackingDraft] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const fetched = await api.admin.order(orderId);
      setOrder(fetched);
      setNotesDraft(fetched.adminNotes || '');
      setTrackingDraft(fetched.trackingNumber || '');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar el pedido');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function changeStatus(nextStatus: OrderStatus) {
    if (!order) return;
    setSavingStatus(true);
    setError('');
    try {
      const updated = await api.admin.updateOrder(order.id, { status: nextStatus });
      setOrder(updated);
      await onUpdated();
    } catch (statusError) {
      setError(statusError instanceof Error ? statusError.message : 'No se pudo cambiar el estado');
    } finally {
      setSavingStatus(false);
    }
  }

  async function saveNotes() {
    if (!order) return;
    setSavingNotes(true);
    setError('');
    try {
      const updated = await api.admin.updateOrder(order.id, {
        adminNotes: notesDraft,
        trackingNumber: trackingDraft
      });
      setOrder(updated);
      await onUpdated();
    } catch (notesError) {
      setError(notesError instanceof Error ? notesError.message : 'No se pudieron guardar las notas');
    } finally {
      setSavingNotes(false);
    }
  }

  async function generateStarken() {
    if (!order) return;
    setGeneratingStarken(true);
    setError('');
    try {
      const result = await api.admin.starkenLabel(order.id);
      const updated = await api.admin.updateOrder(order.id, {
        trackingNumber: result.trackingNumber,
        trackingProvider: 'starken'
      });
      setOrder(updated);
      setTrackingDraft(result.trackingNumber);
      if (result.labelUrl) {
        window.open(result.labelUrl, '_blank', 'noopener');
      }
      await onUpdated();
    } catch (starkenError) {
      setError(starkenError instanceof Error ? starkenError.message : 'No se pudo generar la guia Starken');
    } finally {
      setGeneratingStarken(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <header className="modal-head">
          <div>
            <p className="eyebrow">Pedido</p>
            <h2>{order ? `#${order.orderNumber}` : 'Cargando...'}</h2>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        </header>

        {loading && <p className="muted">Cargando pedido...</p>}
        {error && <p className="form-message tone-error">{error}</p>}

        {order && (
          <>
            <div className="modal-grid">
              <section>
                <h3>Cliente</h3>
                <p>
                  <strong>{order.customerName}</strong>
                  <br />
                  {order.customerEmail}
                  <br />
                  {order.customerPhone || 'Sin telefono'}
                  {order.customerRut && (
                    <>
                      <br />
                      RUT: {order.customerRut}
                    </>
                  )}
                </p>
              </section>
              <section>
                <h3>Despacho</h3>
                <p>
                  {order.shippingAddress || 'Sin direccion'}
                  <br />
                  {[order.shippingCity, order.shippingRegion].filter(Boolean).join(' / ') || ''}
                  {order.shippingNotes && (
                    <>
                      <br />
                      <em>Nota: {order.shippingNotes}</em>
                    </>
                  )}
                </p>
              </section>
              <section>
                <h3>Estado</h3>
                <div className="status-actions">
                  <OrderStatusPill status={order.status} />
                  <select
                    value={order.status}
                    disabled={savingStatus}
                    onChange={(event) => changeStatus(event.target.value as OrderStatus)}
                  >
                    {NEXT_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="muted">
                  Creado {new Date(order.createdAt).toLocaleString('es-CL')}
                </p>
              </section>
              <section>
                <h3>Pago</h3>
                <p>
                  {order.paymentProvider === 'mercadopago' ? 'MercadoPago' : order.paymentProvider}
                  <br />
                  {order.paymentId ? `ID: ${order.paymentId}` : 'Sin payment id'}
                </p>
              </section>
            </div>

            <section className="modal-items">
              <h3>Productos</h3>
              <div className="items-table">
                <div className="items-row items-head">
                  <span>Producto</span>
                  <span>Variante</span>
                  <span>Cant.</span>
                  <span>Unitario</span>
                  <span>Total</span>
                </div>
                {(order.items || []).map((item) => (
                  <div className="items-row" key={item.id}>
                    <span>{item.name}</span>
                    <span>{[item.color, item.size].filter(Boolean).join(' / ') || '-'}</span>
                    <span>{item.quantity}</span>
                    <span>{formatCLP(item.unitPrice)}</span>
                    <span>{formatCLP(item.total)}</span>
                  </div>
                ))}
              </div>
              <dl className="totals">
                <div><dt>Subtotal</dt><dd>{formatCLP(order.subtotal)}</dd></div>
                <div><dt>Despacho</dt><dd>{formatCLP(order.shippingTotal)}</dd></div>
                {order.discountTotal > 0 && (
                  <div><dt>Descuento</dt><dd>-{formatCLP(order.discountTotal)}</dd></div>
                )}
                <div className="total-row"><dt>Total</dt><dd>{formatCLP(order.total)}</dd></div>
              </dl>
            </section>

            <section>
              <h3>Despacho y notas</h3>
              <label>
                Numero de seguimiento
                <input
                  value={trackingDraft}
                  onChange={(event) => setTrackingDraft(event.target.value)}
                  placeholder="Ej: STK123456789"
                />
              </label>
              <label>
                Notas internas
                <textarea
                  rows={3}
                  value={notesDraft}
                  onChange={(event) => setNotesDraft(event.target.value)}
                  placeholder="Para uso interno: observaciones, intentos de contacto, etc."
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="primary-button compact" onClick={saveNotes} disabled={savingNotes}>
                  {savingNotes ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </section>

            <section className="modal-shipping-actions">
              <h3>Etiqueta y guia</h3>
              <div className="shipping-actions-row">
                <button type="button" className="primary-button compact" onClick={() => onPrintLabel(order)}>
                  Imprimir etiqueta simple
                </button>
                {features.starkenConfigured ? (
                  <button
                    type="button"
                    className="ghost-button compact"
                    onClick={generateStarken}
                    disabled={generatingStarken}
                  >
                    {generatingStarken ? 'Generando...' : 'Generar guia Starken'}
                  </button>
                ) : (
                  <button type="button" className="ghost-button compact" disabled title="Configura Starken en Cloudflare para activar">
                    Generar guia Starken (configurar)
                  </button>
                )}
              </div>
              {!features.starkenConfigured && (
                <p className="muted">
                  Para activar el boton de Starken, agrega <code>STARKEN_CLIENTE_ID</code>, <code>STARKEN_API_TOKEN</code> y{' '}
                  <code>STARKEN_API_URL</code> como secretos de Cloudflare. Mientras tanto puedes imprimir la etiqueta simple
                  y llevar el paquete a la sucursal.
                </p>
              )}
              {order.trackingNumber && (
                <p>
                  <strong>Tracking actual:</strong> {order.trackingNumber}{' '}
                  {order.trackingProvider && <em>({order.trackingProvider})</em>}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function LabelPrintOverlay({
  orders,
  sender,
  onClose
}: {
  orders: Order[];
  sender: StoreSender;
  onClose: () => void;
}) {
  const [hydrated, setHydrated] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const detailed = await Promise.all(
          orders.map(async (order) => {
            if (order.items && order.items.length) return order;
            try {
              return await api.admin.order(order.id);
            } catch {
              return order;
            }
          })
        );
        if (active) setHydrated(detailed);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [orders]);

  function doPrint() {
    window.print();
  }

  return (
    <div className="print-overlay" role="dialog" aria-label="Vista de impresion">
      <header className="print-toolbar no-print">
        <div>
          <strong>Etiquetas listas para imprimir</strong>
          <p className="muted">
            {orders.length} {orders.length === 1 ? 'etiqueta' : 'etiquetas'}. Configura la impresora a tamano carta o A4 sin margenes para mejor resultado.
          </p>
        </div>
        <div>
          <button type="button" className="ghost-button compact" onClick={onClose}>Cerrar</button>
          <button type="button" className="primary-button compact" onClick={doPrint} disabled={loading}>
            {loading ? 'Cargando...' : 'Imprimir'}
          </button>
        </div>
      </header>

      <div className="print-area">
        {hydrated.map((order) => (
          <article className="shipping-label" key={order.id}>
            <header className="shipping-label-head">
              <div className="shipping-sender">
                <p className="shipping-eyebrow">Remitente</p>
                <strong>{sender.name}</strong>
                <span>{sender.address}</span>
                <span>{sender.city}, {sender.region}</span>
                <span>{sender.phone}</span>
              </div>
              <div className="shipping-order-no">
                <p className="shipping-eyebrow">Pedido</p>
                <strong>#{order.orderNumber}</strong>
                <span>{new Date(order.createdAt).toLocaleDateString('es-CL')}</span>
              </div>
            </header>

            <section className="shipping-recipient">
              <p className="shipping-eyebrow">Destinatario</p>
              <strong>{order.customerName}</strong>
              <p>{order.shippingAddress}</p>
              <p>{[order.shippingCity, order.shippingRegion].filter(Boolean).join(' / ')}</p>
              <p>Tel: {order.customerPhone || 'Sin telefono'}</p>
              {order.customerRut && <p>RUT: {order.customerRut}</p>}
            </section>

            <section className="shipping-items">
              <p className="shipping-eyebrow">Contenido</p>
              <ul>
                {(order.items || []).map((item) => (
                  <li key={item.id}>
                    {item.quantity} × {item.name}
                    {(item.color || item.size) && ` (${[item.color, item.size].filter(Boolean).join(' / ')})`}
                  </li>
                ))}
              </ul>
              {order.shippingNotes && <p className="shipping-notes">Nota: {order.shippingNotes}</p>}
            </section>

            <footer className="shipping-footer">
              <span>Total: {formatCLP(order.total)}</span>
              <span>{sender.email}</span>
            </footer>
          </article>
        ))}
      </div>
    </div>
  );
}
